import React, { useEffect, useState } from "react";

import {
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    CircularProgress,
    Box,
} from "@mui/material";

import { Bar, Doughnut } from "react-chartjs-2";

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    ArcElement,
    Tooltip,
    Legend,
} from "chart.js";

import { getTrialReportAction } from "../../Actions/ReportsAction";
import CustomSnackbar from "../Common/snackBar";
import FilterDrawer from "../../components/DataTable/FilterDrawer";
import { TableFilte } from "../../Assets/svg/svgicon";

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const drawerFilters = [
    {
        name: "product",
        label: "Portal",
        type: "select",
        defaultValue: "all",
        options: [
            { label: "All Portals", value: "all" },
            { label: "QBot", value: "qbot" },
            { label: "PayCentral", value: "paycentral" },
            { label: "KYC", value: "kyc" },
            { label: "Tripola", value: "tripola" },
        ],
    },
    {
        name: "status",
        label: "Status",
        type: "status",
        defaultValue: "all",
        options: [
            { label: "All Status", value: "all" },
            { label: "Active", value: "active" },
            { label: "Expired", value: "expired" },
        ],
    },
    {
        name: "dateRange",
        label: "Date Range",
        type: "dateRange",
        placeholder: "Select date range",
    },
];

const TrialReports = () => {
    const [loading, setLoading] = useState(false);

    const getDefaultDateRange = () => {
        const to = new Date();
        const from = new Date();
        from.setMonth(from.getMonth() - 6);
        return { from, to };
    };

    const [filterValues, setFilterValues] = useState({
        product: "all",
        status: "all",
        dateRange: getDefaultDateRange(),
    });

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const [summary, setSummary] = useState({
        totalTrials: 0,
        activeTrials: 0,
        expiredTrials: 0,
    });

    const [chartData, setChartData] = useState({
        status: { active: 0, expired: 0 },
        productCount: { qbot: 0, paycentral: 0, kyc: 0, tripola: 0 },
    });

    const [trialList, setTrialList] = useState([]);

    const buildApiFilters = (fv) => ({
        product: fv.product === "all" ? "" : fv.product,
        status: fv.status === "all" ? "" : fv.status,
        startDate: fv.dateRange?.from ? fv.dateRange.from.toISOString().split("T")[0] : "",
        endDate: fv.dateRange?.to ? fv.dateRange.to.toISOString().split("T")[0] : "",
    });

    const fetchTrialReport = async (fv = filterValues) => {
        try {
            setLoading(true);
            const response = await getTrialReportAction(buildApiFilters(fv));
            if (response?.status === 200) {
                setSummary(response.data.summary);
                setChartData(response.data.charts);
                setTrialList(response.data.trials || []);
            }
        } catch (error) {
            setSnackbar({
                open: true,
                message: error?.response?.data?.message || "Something went wrong",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTrialReport();
    }, []);

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFilterApply = (newValues) => {
        setFilterValues(newValues);
        fetchTrialReport(newValues);
    };

    const handleFilterReset = () => {
        const resetValues = { product: "all", status: "all", dateRange: getDefaultDateRange() };
        setFilterValues(resetValues);
        fetchTrialReport(resetValues);
    };

    const hasAppliedFilters =
        filterValues.product !== "all" ||
        filterValues.status !== "all";

    const statusChartData = {
        labels: ["Active", "Expired"],
        datasets: [
            {
                data: [chartData?.status?.active || 0, chartData?.status?.expired || 0],
                backgroundColor: ["#16a34a", "#ef4444"],
            },
        ],
    };

    const productChartData = {
        labels: ["QBot", "PayCentral", "KYC", "Tripola"],
        datasets: [
            {
                label: "Trial Count",
                data: [
                    chartData?.productCount?.qbot || 0,
                    chartData?.productCount?.paycentral || 0,
                    chartData?.productCount?.kyc || 0,
                    chartData?.productCount?.tripola || 0,
                ],
                backgroundColor: ["#22419e", "#16a34a", "#f59e0b", "#ef4444"],
            },
        ],
    };

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <div className="card border-rounded-0 fiter-card">
                        <div className="card-body p-5 pb-0">
                            <div className="row align-items-center mb-5">
                                <div className="col-auto">
                                    <h2 className="chart-title mb-0">Trial Report</h2>
                                </div>

                                <div className="col-auto ms-auto d-flex gap-2">
                                    <Button
                                        variant="outlined"
                                        onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                                        startIcon={<TableFilte />}
                                        className={`dt-filter-btn ${hasAppliedFilters ? "active" : ""}`}
                                    >
                                        Filters
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#EEF4FF", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Total Trials</Typography>
                            <Typography variant="h4">{summary.totalTrials}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#e9f8f0", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Active Trials</Typography>
                            <Typography variant="h4">{summary.activeTrials}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: "#f8f1f3", boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6">Expired Trials</Typography>
                            <Typography variant="h4">{summary.expiredTrials}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                {/* CHARTS */}
                <Grid container spacing={3} mt={1} sx={{ width: "100%", marginLeft: 0 }}>
                    <Grid item xs={12} md={8} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Trials By Product
                                </Typography>
                                <Box height={350}>
                                    <Bar
                                        data={productChartData}
                                        options={{ responsive: true, maintainAspectRatio: false }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={4} sx={{ display: "flex" }}>
                        <Card sx={{ borderRadius: 3, height: "100%", width: "100%", overflow: "hidden" }}>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Trial Status
                                </Typography>
                                <Box height={350}>
                                    <Doughnut
                                        data={statusChartData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            plugins: { legend: { position: "bottom" } },
                                        }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" fontWeight={700} mb={3}>
                                    Trial Details
                                </Typography>
                                <div className="table-responsive">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th> Tenant </th>
                                                <th> Product </th>
                                                <th> Trial Start </th>
                                                <th> Trial End </th>
                                                <th> Status </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={5} className="text-center">
                                                        <CircularProgress />
                                                    </td>
                                                </tr>
                                            ) : trialList.length > 0 ? (
                                                trialList.map((item, index) => (
                                                    <tr key={index}>
                                                        <td>{item.tenant}</td>
                                                        <td>{item.product}</td>
                                                        <td>{item.trialStart}</td>
                                                        <td>{item.trialEnd}</td>
                                                        <td>
                                                            <span
                                                                className={`badge ${item.status === "Active" ? "bg-success" : "bg-danger"}`}
                                                            >
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={5} className="text-center">
                                                        No Data Found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Grid>

            {/* Filter Drawer */}
            <FilterDrawer
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => setFilterAnchorEl(null)}
                filters={drawerFilters}
                filterValues={filterValues}
                onApply={handleFilterApply}
                onReset={handleFilterReset}
            />

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleCloseSnackbar}
            />
        </>
    );
};

export default TrialReports;